import { useState, useEffect } from 'react';
import { useMembershipStore } from '@/stores/membershipStore';

export interface DailyCheckInModuleProps {
  onCheckInSuccess?: () => void;
  refreshTrigger?: number;
}

export function DailyCheckInModule({ onCheckInSuccess, refreshTrigger }: DailyCheckInModuleProps) {
  const {
    checkInStatus,
    isCheckInLoading,
    fetchCheckInStatus: loadCheckInStatus,
    doCheckIn,
  } = useMembershipStore();

  const [toastMsg, setToastMsg] = useState<string>('');

  const showToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  useEffect(() => {
    void loadCheckInStatus();
  }, [loadCheckInStatus, refreshTrigger]);

  // 签到操作
  const handleCheckIn = async () => {
    if (checkInStatus?.checked_in) {
      showToast('今日已签到过，明天再来吧！');
      return;
    }

    try {
      const res = await doCheckIn();
      if (res) {
        const gained = res.award_amount ?? 10;
        showToast(`签到成功！获得 +${gained} 积分`);
        if (onCheckInSuccess) {
          onCheckInSuccess();
        }
      }
    } catch (err: any) {
      showToast(err?.message || '签到失败，请稍后重试');
    }
  };

  // 整理 7 天周期的奖励数据
  const defaultSchedule = [10, 15, 20, 25, 30, 40, 50];
  const scheduleData = Array.from({ length: 7 }, (_, i) => {
    if (Array.isArray(checkInStatus?.reward_schedule) && checkInStatus.reward_schedule[i] !== undefined) {
      return checkInStatus.reward_schedule[i];
    }
    return defaultSchedule[i] || (i + 1) * 10;
  });

  // 判断每日状态
  const cycleDay = checkInStatus?.cycle_day || 0;
  const isCheckedInToday = Boolean(checkInStatus?.checked_in);

  // 今日待签到的天数索引 (1~7)
  let todayClaimableDay = 1;
  if (isCheckedInToday) {
    todayClaimableDay = cycleDay; // 今日已签到的天数
  } else {
    todayClaimableDay = (cycleDay % 7) + 1; // 今日待签到的天数
  }

  return (
    <div className="bg-gradient-to-br from-emerald-50/50 via-white to-amber-50/30 rounded-2xl p-4 sm:p-6 mb-6 text-slate-900 shadow-2xs relative overflow-hidden border border-emerald-100/80">
      {/* Toast 内部提示 */}
      {toastMsg && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999] px-4 py-2 bg-slate-900 text-white text-xs font-semibold rounded-full shadow-2xl animate-[fadeIn_0.2s_ease-out] flex items-center gap-2 border border-slate-800">
          <i className="ri-checkbox-circle-fill text-emerald-400 text-sm"></i>
          <span>{toastMsg}</span>
        </div>
      )}

      {/* 头部标题与操作 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80 flex items-center gap-1">
              <i className="ri-calendar-check-line text-xs"></i>
              每日签到领积分
            </span>
            <span className="text-xs text-slate-500 font-mono">
              连续签到：<strong className="text-amber-600 font-bold">{cycleDay}</strong> 天
            </span>
          </div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            {isCheckedInToday ? '今日签到已完成 🎉' : '每日签到 · 积分大放送'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {isCheckedInToday
              ? `明天继续签到可领 +${checkInStatus?.next_award_amount ?? scheduleData[todayClaimableDay % 7]} 积分`
              : `今日签到可领 +${checkInStatus?.next_award_amount ?? scheduleData[todayClaimableDay - 1]} 积分，连续 7 天送大礼包！`}
          </p>
        </div>

        <button
          onClick={handleCheckIn}
          disabled={isCheckInLoading || isCheckedInToday}
          className={`px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-md flex items-center justify-center gap-2 shrink-0 cursor-pointer disabled:opacity-85 ${
            isCheckedInToday
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/80 cursor-not-allowed'
              : 'bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-600 hover:to-teal-600 text-white shadow-emerald-500/20 active:scale-95'
          }`}
        >
          <i
            className={
              isCheckedInToday
                ? 'ri-checkbox-circle-fill text-base text-emerald-600'
                : 'ri-calendar-check-fill text-base'
            }
          ></i>
          {isCheckInLoading
            ? '签到提交中...'
            : isCheckedInToday
              ? '今日已签到'
              : checkInStatus?.next_award_amount
                ? `立即签到 (+${checkInStatus.next_award_amount}积分)`
                : '立即签到'}
        </button>
      </div>

      {/* 7日签到周期网格卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 pt-1">
        {scheduleData.map((pts, idx) => {
          const dayNum = idx + 1;

          // 状态判定逻辑:
          // 1. 已完成 (isPassed): 当天数小于当前已连续签到的周期天数，或正好是今天且今天已签到
          // 2. 今日待领 (isTodayActive): 当天数等于 todayClaimableDay 且今日未签到
          // 3. 未来解锁 (isFuture): 当天数大于 todayClaimableDay
          let isPassed = false;
          let isTodayActive = false;

          if (isCheckedInToday) {
            isPassed = dayNum <= cycleDay;
          } else {
            isPassed = dayNum < todayClaimableDay;
            if (dayNum === todayClaimableDay) {
              isTodayActive = true;
            }
          }

          const isFinalDay = dayNum === 7;

          return (
            <div
              key={dayNum}
              className={`relative rounded-xl p-3 flex flex-col items-center justify-between text-center transition-all border ${
                isTodayActive
                  ? 'bg-gradient-to-b from-amber-50 to-orange-50/80 border-amber-400 shadow-md shadow-amber-500/10 ring-2 ring-amber-400/40 -translate-y-1'
                  : isPassed
                    ? 'bg-emerald-50/70 border-emerald-200/80 text-emerald-800'
                    : isFinalDay
                      ? 'bg-gradient-to-b from-amber-50/40 to-orange-50/30 border-amber-200 text-amber-800'
                      : 'bg-slate-50/80 border-slate-200/70 text-slate-600'
              }`}
            >
              {/* 角标标识 */}
              {isTodayActive && (
                <span className="absolute -top-2 px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-500 text-white shadow-xs animate-bounce">
                  今日领
                </span>
              )}
              {isFinalDay && !isTodayActive && !isPassed && (
                <span className="absolute -top-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                  大礼包
                </span>
              )}

              {/* 天数 */}
              <div className="text-[11px] font-medium text-slate-500 mb-1.5">
                第 {dayNum} 天
              </div>

              {/* 图标与积分数 */}
              <div className="my-1.5 flex flex-col items-center">
                {isPassed ? (
                  <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 border border-emerald-200 flex items-center justify-center text-base mb-1">
                    <i className="ri-check-line font-bold"></i>
                  </div>
                ) : isFinalDay ? (
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 border border-amber-200 flex items-center justify-center text-base mb-1">
                    <i className="ri-gift-fill text-amber-500"></i>
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full bg-slate-100 text-amber-500 border border-slate-200/60 flex items-center justify-center text-sm mb-1">
                    <i className="ri-coins-line"></i>
                  </div>
                )}

                <span
                  className={`text-xs font-bold font-mono ${
                    isTodayActive
                      ? 'text-amber-600 text-sm font-extrabold'
                      : isPassed
                        ? 'text-emerald-700'
                        : 'text-slate-700'
                  }`}
                >
                  +{pts}
                </span>
              </div>

              {/* 底部状态文案 */}
              <div className="text-[10px] mt-1 font-medium">
                {isPassed ? (
                  <span className="text-emerald-600">已完成</span>
                ) : isTodayActive ? (
                  <span className="text-amber-600 font-bold">可领取</span>
                ) : (
                  <span className="text-slate-400">未解锁</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DailyCheckInModule;
